import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { BundleFloorBinLevel } from '../../../schemas/apiResponse.schemas';
import { parseBundleBinSplit, type BundleBinSplitEntry } from './bundleWorkOrderLogic';

interface BundleWorkCompleteModalProps {
  readonly open: boolean;
  readonly parentSku: string;
  readonly bins: readonly BundleFloorBinLevel[];
  readonly suggestedQty: number | null;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (entries: readonly BundleBinSplitEntry[]) => void;
}

type BinCountField = 'adding' | 'total';

interface BinCountDraft {
  readonly adding: string;
  readonly total: string;
  readonly committedAdding: string;
  readonly committedTotal: string;
}

interface BinFocusSnapshot {
  readonly prefix: string;
  readonly field: BinCountField;
  readonly draft: BinCountDraft;
}

interface LinkedBinCounts {
  readonly adding: string;
  readonly total: string;
}

function binLabel(bin: BundleFloorBinLevel): string {
  return bin.shelfIndex ?? 'Unindexed bin';
}

function initialDraft(quantity: number): BinCountDraft {
  const total = String(quantity);
  return { adding: '0', total, committedAdding: '0', committedTotal: total };
}

/** Adding is a delta from the bin's starting quantity. Total never goes below 0. */
function linkAdding(start: number, raw: string): LinkedBinCounts | null {
  if (!/^-?\d+$/.test(raw)) {
    return null;
  }
  const adding = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(adding)) {
    return null;
  }
  if (start + adding < 0) {
    return { adding: String(-start), total: '0' };
  }
  return { adding: String(adding), total: String(start + adding) };
}

/** Total is the new absolute count. Adding becomes the difference from the start. */
function linkTotal(start: number, raw: string): LinkedBinCounts | null {
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const total = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(total) || total < 0) {
    return null;
  }
  return { adding: String(total - start), total: String(total) };
}

function applyLinked(draft: BinCountDraft, linked: LinkedBinCounts): BinCountDraft {
  return {
    adding: linked.adding,
    total: linked.total,
    committedAdding: linked.adding,
    committedTotal: linked.total,
  };
}

function RowArrow(): React.ReactElement {
  return (
    <svg className="bundle-work-bin-split-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4 12h14M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BundleWorkCompleteModal({
  open,
  parentSku,
  bins,
  suggestedQty,
  submitting,
  error,
  onCancel,
  onConfirm,
}: BundleWorkCompleteModalProps): React.ReactElement | null {
  const inputId = useId();
  const draftsRef = useRef<Readonly<Record<string, BinCountDraft>>>({});
  const focusRef = useRef<BinFocusSnapshot | null>(null);
  const flashTimersRef = useRef<Readonly<Record<string, ReturnType<typeof setTimeout>>>>({});
  const [drafts, setDrafts] = useState<Readonly<Record<string, BinCountDraft>>>({});
  const [flashing, setFlashing] = useState<Readonly<Record<string, true>>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  const writeDrafts = (next: Readonly<Record<string, BinCountDraft>>): void => {
    draftsRef.current = next;
    setDrafts(next);
  };

  const writeDraft = (prefix: string, draft: BinCountDraft): void => {
    writeDrafts({ ...draftsRef.current, [prefix]: draft });
  };

  useEffect(() => {
    return () => {
      for (const timer of Object.values(flashTimersRef.current)) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const next: Record<string, BinCountDraft> = {};
    for (const bin of bins) {
      next[bin.storageIdPrefix] = initialDraft(bin.quantity);
    }
    focusRef.current = null;
    writeDrafts(next);
    setFlashing({});
    setLocalError(null);
  }, [open, bins]);

  const enteredTotal = useMemo(() => {
    const parsed = parseBundleBinSplit(
      bins.map((bin) => ({
        storageIdPrefix: bin.storageIdPrefix,
        value: drafts[bin.storageIdPrefix]?.committedTotal ?? String(bin.quantity),
      })),
    );
    return parsed.ok ? parsed.total : 0;
  }, [bins, drafts]);

  if (!open) {
    return null;
  }

  const displayError = localError ?? error;
  const suggestionText =
    suggestedQty != null && suggestedQty > 0 ? `${suggestedQty} needed to reach baseline` : null;

  const flashRow = (prefix: string): void => {
    setFlashing((prev) => ({ ...prev, [prefix]: true }));
    const existing = flashTimersRef.current[prefix];
    if (existing) {
      clearTimeout(existing);
    }
    const timer = setTimeout(() => {
      setFlashing((prev) => {
        if (!prev[prefix]) {
          return prev;
        }
        const next = { ...prev };
        delete next[prefix];
        return next;
      });
    }, 700);
    flashTimersRef.current = { ...flashTimersRef.current, [prefix]: timer };
  };

  const handleFocus = (prefix: string, field: BinCountField): void => {
    const current = draftsRef.current[prefix];
    if (!current) {
      return;
    }
    focusRef.current = { prefix, field, draft: current };
    writeDraft(prefix, { ...current, [field]: '' });
  };

  const handleBlur = (prefix: string, field: BinCountField, start: number): void => {
    const snap = focusRef.current;
    if (!snap || snap.prefix !== prefix || snap.field !== field) {
      return;
    }
    focusRef.current = null;
    const raw = (draftsRef.current[prefix]?.[field] ?? '').trim();
    if (raw === '') {
      writeDraft(prefix, snap.draft);
      return;
    }
    const linked = field === 'adding' ? linkAdding(start, raw) : linkTotal(start, raw);
    if (!linked) {
      writeDraft(prefix, snap.draft);
      return;
    }
    const next = applyLinked(snap.draft, linked);
    writeDraft(prefix, next);
    if (next.committedAdding !== snap.draft.committedAdding || next.committedTotal !== snap.draft.committedTotal) {
      flashRow(prefix);
    }
  };

  const handleChange = (prefix: string, field: BinCountField, value: string): void => {
    const pattern = field === 'adding' ? /^-?\d*$/ : /^\d*$/;
    if (!pattern.test(value)) {
      return;
    }
    const current = draftsRef.current[prefix];
    if (!current) {
      return;
    }
    writeDraft(prefix, { ...current, [field]: value });
    setLocalError(null);
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const snap = focusRef.current;
    if (snap) {
      const start = bins.find((bin) => bin.storageIdPrefix === snap.prefix)?.quantity;
      if (start !== undefined) {
        handleBlur(snap.prefix, snap.field, start);
      }
    }
    const parsed = parseBundleBinSplit(
      bins.map((bin) => ({
        storageIdPrefix: bin.storageIdPrefix,
        value: draftsRef.current[bin.storageIdPrefix]?.committedTotal ?? '',
      })),
    );
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    setLocalError(null);
    onConfirm(parsed.entries);
  };

  return (
    <div className="pbl-modal-overlay bundle-work-complete-modal-overlay" onClick={onCancel} role="presentation">
      <div
        className="pbl-modal bundle-work-complete-modal bundle-work-complete-modal-bins"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby={`${inputId}-title`}
        aria-modal="true"
      >
        <button type="button" className="pbl-modal-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
        <h3 id={`${inputId}-title`} className="bundle-work-complete-modal-title">
          How many are in each bin?
        </h3>
        {bins.length === 0 ? (
          <>
            <p className="bundle-work-complete-modal-empty">
              {parentSku} has no pick bin. Print and index a pick bin label before recording
              bundles.
            </p>
            <div className="bundle-work-complete-modal-actions">
              <button
                type="button"
                className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-cancel"
                onClick={onCancel}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <form className="bundle-work-complete-modal-form" onSubmit={handleSubmit}>
            {suggestionText ? (
              <p className="bundle-work-complete-modal-hint">{suggestionText}</p>
            ) : null}
            <div className="bundle-work-bin-split-head">
              <span className="bundle-work-bin-split-head-spacer" />
              <div className="bundle-work-bin-split-fields">
                <span className="bundle-work-bin-split-col">Adding</span>
                <span className="bundle-work-bin-split-arrow-slot" />
                <span className="bundle-work-bin-split-col">Total</span>
              </div>
            </div>
            <ul className="bundle-work-bin-split-list">
              {bins.map((bin) => {
                const draft = drafts[bin.storageIdPrefix] ?? initialDraft(bin.quantity);
                const showArrow = draft.committedAdding !== '0';
                const rowClass = flashing[bin.storageIdPrefix]
                  ? 'bundle-work-bin-split-row bundle-work-bin-split-row-flash'
                  : 'bundle-work-bin-split-row';
                return (
                  <li key={bin.storageIdPrefix} className={rowClass}>
                    <div className="bundle-work-bin-split-label">
                      <span className="bundle-work-bin-split-shelf">
                        {binLabel(bin)}
                        {bin.isPrimary ? <span className="bundle-work-bin-split-primary">primary</span> : null}
                      </span>
                      <span className="bundle-work-bin-split-meta">
                        ID {bin.storageIdPrefix} · has {bin.quantity}
                      </span>
                    </div>
                    <div className="bundle-work-bin-split-fields">
                      <input
                        id={`${inputId}-${bin.storageIdPrefix}-adding`}
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCapitalize="off"
                        className="bundle-work-complete-modal-input bundle-work-bin-split-input"
                        value={draft.adding}
                        aria-label={`Adding to bin ${binLabel(bin)}`}
                        onFocus={() => handleFocus(bin.storageIdPrefix, 'adding')}
                        onBlur={() => handleBlur(bin.storageIdPrefix, 'adding', bin.quantity)}
                        onChange={(event) => handleChange(bin.storageIdPrefix, 'adding', event.target.value)}
                        disabled={submitting}
                      />
                      <span className="bundle-work-bin-split-arrow-slot">
                        {showArrow ? <RowArrow /> : null}
                      </span>
                      <input
                        id={`${inputId}-${bin.storageIdPrefix}-total`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        className="bundle-work-complete-modal-input bundle-work-bin-split-input"
                        value={draft.total}
                        aria-label={`Total in bin ${binLabel(bin)}`}
                        onFocus={() => handleFocus(bin.storageIdPrefix, 'total')}
                        onBlur={() => handleBlur(bin.storageIdPrefix, 'total', bin.quantity)}
                        onChange={(event) => handleChange(bin.storageIdPrefix, 'total', event.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="bundle-work-bin-split-total" aria-live="polite">
              Total in bins: {enteredTotal}
            </p>
            {displayError ? (
              <p className="bundle-work-complete-modal-error" role="alert">
                {displayError}
              </p>
            ) : null}
            <div className="bundle-work-complete-modal-actions">
              <button
                type="button"
                className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-cancel"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bundle-work-complete-modal-btn bundle-work-complete-modal-btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BundleWorkCompleteModal;
